import '../support-chat.scss';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type IChatThreadState } from '../../../hooks/useChatThread';
import { useKeyboardInset } from '../../../hooks/useKeyboardInset';
import { useReadReceipts } from '../../../hooks/useReadReceipts';
import { type ChatCounterpart, type IOwnershipContext, type ISupportReplyContext } from '../../../interface/support-chat';
import { counterpartCopy } from '../../../lib/chat-counterpart-copy';
import { repliesSupported } from '../../../lib/support-api';
import { canDeleteMessage, canEditMessage, canReplyToMessage, replyContextFrom } from '../../../lib/support-message-actions';
import { supportReachNotice } from '../../../lib/support-reach-copy';
import MessageActionSheet from '../../utils/message-action-sheet';
import ChatComposer from './ChatComposer';
import ChatQuote from './ChatQuote';
import ChatTranscript from './ChatTranscript';

interface IChatConversation {
	/** The engine for this conversation, owned by the layout and read here. */
	thread: IChatThreadState;
	counterpart: ChatCounterpart;
	/** Shown when the transcript is empty. The support copy is the default. */
	emptyState?: { title: string; body: React.ReactNode };
	/** One muted line above the first message — what the conversation is about. */
	preamble?: string | null;
	/** Rendered above the transcript, under the reach banners: the caller's own notices. */
	banner?: React.ReactNode;
	/** Quick messages. A chip inserts its text into an EMPTY composer and focuses it; never sends. */
	quickMessages?: string[];
	/** Text the composer starts with, seeded once on mount. Never auto-sent. */
	seedDraft?: string;
	/** Refuse input beyond what the engine already decided (a reassignment the roster reported). */
	composerDisabled?: boolean;
}

/**
 * One conversation: banners, transcript, typing line, action sheet, reply/edit strips, composer.
 *
 * <p>A VIEW over state owned by the Dashboard layout, not a state owner. The socket and the
 * outbox must outlive the panel — an agent closes the chat to go upload a result sheet and
 * their in-flight message has to survive that.</p>
 *
 * <p>Knows nothing about WHO the other party is beyond the {@link ChatCounterpart} it is handed:
 * the support panel passes the desk, an agent↔agent panel passes a person, and every sentence
 * that names them comes from one copy table so the two surfaces cannot drift.</p>
 */
const ChatConversation = ({
	thread,
	counterpart,
	emptyState,
	preamble,
	banner,
	quickMessages,
	seedDraft,
	composerDisabled,
}: IChatConversation) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const [actionsFor, setActionsFor] = useState<string | null>(null);
	const [draftEmpty, setDraftEmpty] = useState(!seedDraft);
	const [insert, setInsert] = useState<{ text: string; nonce: number } | null>(null);
	/**
	 * The text in the composer while NOT editing, so leaving edit mode (which remounts the
	 * composer) restores what the agent had typed. Starts as the escalation seed — consumed
	 * here, once, never re-read from the panel data — and is cleared on send.
	 */
	const draftRef = useRef<string>(seedDraft ?? '');
	/**
	 * What the composer is for right now.
	 *
	 * <p>One union rather than an `editing` flag beside a `replyTo` flag, so "you cannot edit and
	 * reply at once" is structural instead of a guard every new branch has to remember.</p>
	 */
	const [composerMode, setComposerMode] = useState<
		{ kind: 'compose' } | { kind: 'edit'; id: string; body: string } | { kind: 'reply'; target: ISupportReplyContext }
	>({ kind: 'compose' });
	const editing = composerMode.kind === 'edit' ? composerMode : null;
	const replyTo = composerMode.kind === 'reply' ? composerMode.target : null;
	const setEditing = (next: { id: string; body: string } | null) => {
		// A chip insertion is consumed the moment the composer applies it; a stale one must not
		// be re-applied by the remount that entering or leaving edit mode causes.
		setInsert(null);
		setComposerMode(next ? { kind: 'edit', ...next } : { kind: 'compose' });
	};
	const editingRef = useRef(false);
	editingRef.current = composerMode.kind === 'edit';
	const onDraftChange = useCallback((empty: boolean, text: string) => {
		setDraftEmpty(empty);
		// Only the conversation draft is remembered; the edit body belongs to the message.
		if (!editingRef.current) draftRef.current = text;
	}, []);
	const setReplyTo = (next: ISupportReplyContext | null) =>
		setComposerMode(next ? { kind: 'reply', target: next } : { kind: 'compose' });

	/**
	 * Does this api.vesr return a reply target once it has stored one?
	 *
	 * <p>The write field has shipped since the support routes did, so any backend serving this chat
	 * accepts `replyToId`. The READ path is the newer half, and the app routinely runs against an
	 * older pod than itself — without this the Reply action would send a message whose anchor is
	 * stored and never rendered, on either side, with nothing to say so.</p>
	 */
	const repliesAvailable = repliesSupported(thread.messages) === true;
	const [actionError, setActionError] = useState('');

	// The panel being mounted IS the thread being active — it unmounts when closed.
	const { observe } = useReadReceipts(true, (upTo) => thread.reportRead(upTo));

	// Publishes --kb-inset so the sticky composer pads itself above the Android keyboard.
	// Re-pins the transcript in the same frame, otherwise opening the keyboard pushes the
	// newest message up behind it.
	useKeyboardInset(rootRef, () => {
		const transcript = rootRef.current?.querySelector('.support-chat__transcript');
		if (transcript) transcript.scrollTop = transcript.scrollHeight;
	});

	const { open } = thread;
	useEffect(() => {
		open();
		// Once, on mount: opening is user intent and clears the start backoff.
	}, []);

	const ownership: IOwnershipContext = { me: thread.me, kind: thread.kind, ownClientIds: thread.ownClientIds };
	const copy = counterpartCopy(counterpart);
	const reachNotice = supportReachNotice(thread.reach, counterpart);

	return (
		<div
			className='support-chat'
			ref={rootRef}
		>
			{!thread.online ? (
				<div className='sc-banner sc-banner--offline'>No connection. Your messages will send when you&apos;re back online.</div>
			) : null}

			{thread.online && thread.liveStatus === 'reconnecting' ? (
				<div className='sc-banner sc-banner--reconnecting'>Reconnecting…</div>
			) : null}

			{/* Server reachability is a DIFFERENT question from navigator.onLine, and this is the
			    banner that was missing: throughout the delivery bug the device was online, so the
			    offline banner never fired and nothing else said a word. Offline wins when both
			    apply — it is the more actionable fact. */}
			{thread.online && reachNotice ? (
				<div className='sc-banner sc-banner--unavailable'>
					{reachNotice.message}
					{reachNotice.canRetry ? (
						<button
							type='button'
							className='sc-banner__action'
							onClick={thread.retryThread}
						>
							Try again
						</button>
					) : null}
				</div>
			) : null}

			{thread.storageFull ? (
				<div className='sc-banner sc-banner--offline'>
					Your phone is out of storage. Send the messages you have, or clear space, before adding more.
				</div>
			) : null}

			{actionError ? <div className='sc-banner sc-banner--offline'>{actionError}</div> : null}

			{banner}

			<ChatTranscript
				messages={thread.messages}
				ownership={ownership}
				counterpart={counterpart}
				emptyState={emptyState}
				observeRead={observe}
				onOpenActions={(id) => {
					setActionError('');
					setActionsFor(id);
				}}
				// Only where the server reads reply targets back — otherwise the reply would detach
				// from the result it answers, which is the one thing this button is for.
				onReplyTo={
					repliesAvailable
						? (id) => {
								const target = thread.messages.find((m) => m.id === id);
								if (target) setReplyTo(replyContextFrom(target, ownership));
							}
						: undefined
				}
				onRetry={thread.retry}
				localPreviews={thread.localPreviews}
				uploadPercents={thread.uploadPercents}
				needsReattach={thread.needsReattach}
				onReattach={thread.reattach}
				thumbnails={thread.thumbnails}
				preamble={preamble}
			/>

			{thread.otherIsTyping ? <div className='sc-typing'>{copy.typingLine}</div> : null}

			{/* Only while the field is empty and nothing is being edited or answered: a chip is a
			    way to START, and it must never overwrite half a sentence. */}
			{quickMessages &&
			quickMessages.length > 0 &&
			draftEmpty &&
			composerMode.kind === 'compose' &&
			!thread.composerDisabled &&
			!composerDisabled ? (
				<div
					className='sc-chips'
					role='group'
					aria-label='Quick messages'
				>
					{quickMessages.map((text) => (
						<button
							key={text}
							type='button'
							className='sc-chip'
							onClick={() => setInsert({ text, nonce: Date.now() })}
						>
							{text}
						</button>
					))}
				</div>
			) : null}

			{actionsFor
				? (() => {
						const target = thread.messages.find((m) => m.id === actionsFor);
						if (!target) return null;

						const edit = canEditMessage(target, ownership);
						const remove = canDeleteMessage(target, ownership);
						// Reply is offered on EITHER side, and only when the server will read the
						// target back — sending an anchor that is stored and never rendered is a
						// silent no-op.
						const reply = repliesAvailable ? canReplyToMessage(target) : { allowed: false };

						// If no action is available there is nothing to show. Opening an empty sheet
						// would be worse than not responding to the long-press. Reply now counts:
						// before it existed, this returned null for every desk message, so a
						// long-press on one appeared to be broken.
						if (!reply.allowed && !edit.allowed && !remove.allowed) return null;

						return (
							<MessageActionSheet
								canReply={reply.allowed}
								canEdit={edit.allowed}
								canDelete={remove.allowed}
								onReply={() => {
									// Deliberately does NOT touch the draft: half a sentence typed before
									// deciding which message it answers is still the sentence you meant.
									setReplyTo(replyContextFrom(target, ownership));
									setActionsFor(null);
								}}
								onClose={() => setActionsFor(null)}
								onEdit={() => {
									setEditing({ id: actionsFor, body: target.body });
									setActionsFor(null);
								}}
								onDelete={() => {
									setActionsFor(null);
									void thread.deleteMessage(actionsFor).then((error) => {
										if (error) setActionError(error);
									});
								}}
							/>
						);
					})()
				: null}

			{replyTo ? (
				<div className='sc-replying'>
					<ChatQuote
						reply={replyTo}
						variant='composer'
						ownership={ownership}
						counterpart={counterpart}
					/>
					<button
						type='button'
						className='sc-replying__cancel'
						// Cancels the reply and leaves the draft alone. The composer's own Escape rule
						// exists because losing a draft is unrecoverable; losing a reply target is not.
						onClick={() => setReplyTo(null)}
						aria-label='Cancel reply'
					>
						✕
					</button>
				</div>
			) : null}

			{editing ? (
				<div className='sc-editing'>
					<span>Editing message</span>
					<button
						type='button'
						onClick={() => setEditing(null)}
					>
						Cancel
					</button>
				</div>
			) : null}

			<ChatComposer
				key={editing?.id ?? 'compose'}
				initialBody={editing?.body ?? draftRef.current}
				sendLabel={editing ? 'Save' : 'Send'}
				counterpart={counterpart}
				disabled={thread.composerDisabled || composerDisabled}
				insert={editing ? null : insert}
				onInserted={() => setInsert(null)}
				onDraftChange={onDraftChange}
				onSend={
					editing
						? (body) => {
								const target = editing.id;
								setEditing(null);
								void thread.editMessage(target, body).then((error) => {
									if (error) setActionError(error);
								});
							}
						: (body, file) => {
								// Cleared here rather than inside send(): the outbox item has already
								// taken its own copy, so a retry hours later still answers the same
								// message even though the composer has moved on.
								setReplyTo(null);
								draftRef.current = '';
								thread.send(body, file, replyTo);
							}
				}
				onTyping={thread.notifyTyping}
				pendingAttachmentCount={thread.pendingAttachmentCount}
			/>
		</div>
	);
};

export default ChatConversation;
